/**
 * TriageProjectGrid Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Displays triage project data in a responsive card grid layout with pagination, modal functionality,
 * and status-based visualization. Each card shows project details with date ranges, status indicators,
 * and interactive modal access for detailed project information.
 * 
 * Features:
 * - Responsive card grid layout for project display
 * - Status-based sorting (Live/Active projects first)
 * - Pagination with configurable page sizes including "All" option
 * - Interactive project modals with stacking support
 * - Status indicators with FontAwesome icons
 * - Date range display with proper formatting
 * - Tooltips for truncated content
 * - No-results state handling
 * - Automatic modal positioning and z-index management
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of project objects to display
 * @param {number|string} [props.pageSize=6] - Number of items per page or "All" for no pagination
 * @returns {JSX.Element} The rendered triage project grid component
 * 
 * @example
 * ```jsx
 * <TriageProjectGrid 
 *   data={projectData} 
 *   pageSize={12} 
 * />
 * ```
 */

import React, { useState, useMemo } from 'react';
import TriageProjectModal from '../../modals/TriageProjectModal';
import { truncateText, removeTime } from '../../utils/textUtils';
import './TriageProjectPipeline.css';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faCheckCircle, faRocket, faMoon } from '@fortawesome/free-solid-svg-icons';


const TriageProjectGrid = ({ data, pageSize: initialPageSize = 6 }) => {
    const [activeModals, setActiveModals] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(initialPageSize);

    /**
     * Open a project modal or bring existing modal to front
     * 
     * Manages multiple project modals with proper z-index stacking and positioning.
     * If a modal for the same project already exists, it brings it to the front and reopens it.
     * Otherwise, creates a new modal with calculated positioning to avoid overlap.
     * 
     * @param {Object} itemData - Complete project data object to display in modal
     * @param {string|number} itemData.id - Unique identifier for the project
     * 
     * @example
     * ```javascript
     * handleShowModal({
     *   id: 'project-123',
     *   name: 'Project Name',
     *   status: 'Live',
     *   // ... other project properties
     * })
     * ```
     */
    const handleShowModal = (itemData) => {
        const existingModalIndex = activeModals.findIndex(modal => modal.id === itemData.id);
        if (existingModalIndex !== -1) {
            const updatedModals = [...activeModals];
            const existingModal = updatedModals[existingModalIndex];

            existingModal.zIndex = activeModals.length + 1;
            existingModal.isOpen = true;

            updatedModals.splice(existingModalIndex, 1);
            updatedModals.push(existingModal);

            setActiveModals(updatedModals);
        } else {
            const newModal = {
                id: itemData.id,
                data: itemData,
                isOpen: true,
                top: `${15 + (activeModals.length * 2)}%`,
                left: `${30 + (activeModals.length * 2)}%`,
                zIndex: activeModals.length + 1,
            };
            setActiveModals(prev => [...prev, newModal]);
        }
    };

    /**
     * Close a project modal by ID
     * 
     * Marks the specified modal as closed and removes it from the active modals array.
     * This completely removes the modal rather than just hiding it.
     * 
     * @param {string|number} id - Unique identifier of the modal to close
     * 
     * @example
     * ```javascript
     * handleCloseModal('project-123')
     * ```
     */
    const handleCloseModal = (id) => {
        setActiveModals(prev =>
            prev.map(modal =>
                modal.id === id ? { ...modal, isOpen: false } : modal
            )
        );
        setActiveModals(prev => prev.filter(modal => modal.id !== id));
    };

    /**
     * Get status display information with appropriate icon and text
     * 
     * Maps project status values to consistent display formats with FontAwesome icons.
     * Handles various status formats and provides fallback for unknown statuses.
     * 
     * @param {string} status - The project status value
     * @returns {Object} Object containing FontAwesome icon and display text
     * @returns {Object} returns.icon - FontAwesome icon component
     * @returns {string} returns.text - Formatted status text for display
     * 
     * @example
     * ```javascript
     * getStatusInfo('completed') 
     * // Returns: { icon: faCheckCircle, text: 'Complete' }
     * 
     * getStatusInfo('in progress') 
     * // Returns: { icon: faRocket, text: 'in progress' }
     * 
     * getStatusInfo('unknown') 
     * // Returns: { icon: faRocket, text: 'unknown' }
     * ```
     */
    const getStatusInfo = (status) => {
        const statusLower = status ? status.toLowerCase() : 'unknown';
        switch (statusLower) {
            case 'completed':
            case 'complete':
                return { icon: faCheckCircle, text: 'Complete' };
            case 'in progress':
            case 'active':
            case 'live':
                return { icon: faRocket, text: status };
            case 'inactive':
                return { icon: faMoon, text: status };
            default:
                return { icon: faRocket, text: status || 'Unknown Status' };
        }
    };

    /**
     * Pagination calculations and data processing
     * 
     * Handles sorting, pagination, and data slicing for the current view.
     * Sorts data to prioritize Live/Active projects, then by most recent dates.
     * Calculates pagination boundaries and returns data for the current page.
     * 
     * @returns {Object} Pagination information and current page data
     * @returns {number} returns.totalItems - Total number of items in dataset
     * @returns {number} returns.totalPages - Total number of pages available
     * @returns {Array} returns.currentPageData - Data items for current page
     * @returns {number} returns.startIndex - Starting index for current page
     * @returns {number} returns.endIndex - Ending index for current page
     */
    const paginationInfo = useMemo(() => {
        const sortedData = [...data].sort((a, b) => {
            const aStatus = a.status ? a.status.toLowerCase() : '';
            const bStatus = b.status ? b.status.toLowerCase() : '';
            
            if ((aStatus === 'live' || aStatus === 'active') && (bStatus !== 'live' && bStatus !== 'active')) return -1;
            if ((bStatus === 'live' || bStatus === 'active') && (aStatus !== 'live' && aStatus !== 'active')) return 1;
            
            const aDate = a.dateItemAddedtoTriage ? new Date(a.dateItemAddedtoTriage) : new Date(0);
            const bDate = b.dateItemAddedtoTriage ? new Date(b.dateItemAddedtoTriage) : new Date(0);
            return bDate - aDate;
        });

        const itemsPerPage = pageSize === 'All' ? sortedData.length : parseInt(pageSize);
        const totalPages = pageSize === 'All' ? 1 : Math.ceil(sortedData.length / itemsPerPage);
        const startIndex = currentPage * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const pageData = sortedData.slice(startIndex, endIndex);
        
        return {
            totalItems: sortedData.length,
            totalPages,
            currentPageData: pageData,
            startIndex,
            endIndex
        };
    }, [data, currentPage, pageSize]);

    const visibleData = paginationInfo.currentPageData;
    const totalItems = paginationInfo.totalItems;
    const totalPages = paginationInfo.totalPages;
    const canPreviousPage = currentPage > 0;
    const canNextPage = currentPage < totalPages - 1;

    /**
     * Navigate to previous page
     * 
     * Decrements the current page if not already at the first page.
     * Used by pagination controls to move backward through pages.
     */
    const previousPage = () => {
        if (canPreviousPage) {
            setCurrentPage(currentPage - 1);
        }
    };

    /**
     * Navigate to next page
     * 
     * Increments the current page if not already at the last page.
     * Used by pagination controls to move forward through pages.
     */
    const nextPage = () => {
        if (canNextPage) {
            setCurrentPage(currentPage + 1);
        }
    };

    /**
     * Handle page size change and reset to first page
     * 
     * Updates the page size setting and resets pagination to the first page.
     * This ensures consistent behavior when changing how many items are displayed.
     * 
     * @param {number|string} newPageSize - New page size value or "All"
     */
    const handlePageSizeChange = (newPageSize) => {
        setPageSize(newPageSize);
        setCurrentPage(0);
    };

    return (
        <div className="triage-project-grid-container">

            {/* Cards Grid */}
            <div className="triage-project-cards-wrapper">
                {totalItems === 0 ? (
                    <div className="no-results">
                        <div className="no-results-content">
                            <i className="fas fa-search"></i>
                            <h3>No projects found</h3>
                            <p>Try adjusting your search criteria or filters to see more results.</p>
                        </div>
                    </div>
                ) : (
                    <div className="triage-project-cards-grid">
                    {visibleData.map((item) => {
                        const status = item.status ? item.status.toLowerCase() : 'unknown';
                        const description = item.childName || 'N/A';
                            const statusInfo = getStatusInfo(item.status);
                            const startDate = item.dateItemAddedtoTriage;
                            const endDate = removeTime(item.closeDate);
                            
                        return (
                            <div
                                key={item.id}
                                    className="triage-project-card"
                                onClick={() => handleShowModal(item)}
                            >
                                    {/* Date Header */}
                                    <div className="triage-project-header">
                                        <div className="triage-header-left">
                                            <FontAwesomeIcon 
                                                icon={statusInfo.icon} 
                                                style={{ color: status === 'completed' || status === 'complete' ? 'var(--ifia-green)' : 'var(--ifia-orange)' }} 
                                            />
                                        </div>
                                        <div className="triage-header-right">
                                            <span className="triage-date-info">
                                                <FontAwesomeIcon icon={faCalendarDays} style={{ color: 'var(--ifia-green)' }} />
                                                {startDate && endDate ? `${startDate} - ${endDate}` : startDate || endDate || 'No dates available'}
                                            </span>
                                        </div>
                                </div>

                                    <div className="triage-project-title">
                                        <Tippy content={truncateText(item.name || 'Unknown Name', 120)}>
                                            <h3>
                                                {item.name || 'Unknown Name'}
                                            </h3>
                                        </Tippy>
                                </div>
                                    
                                    <div className="triage-project-content">
                                        <Tippy content={truncateText(description, 250)}>
                                            <p className="triage-project-summary">
                                        {description}
                                    </p>
                                        </Tippy>
                                </div>
                                    
                                    <div className="triage-project-footer">
                                        <Tippy content={`Status: ${statusInfo.text}`}>
                                            <span className={`triage-project-status-tag status-${status}`} style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                fontSize: '0.875rem',
                                                fontWeight: '500',
                                                padding: '0.25rem 0.75rem',
                                                borderRadius: '0.375rem',
                                                backgroundColor: status === 'completed' || status === 'complete' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(251, 146, 60, 0.1)',
                                                color: status === 'completed' || status === 'complete' ? 'var(--ifia-green)' : 'var(--ifia-orange)',
                                                border: `1px solid ${status === 'completed' || status === 'complete' ? 'var(--ifia-green)' : 'var(--ifia-orange)'}`
                                            }}>
                                                <FontAwesomeIcon icon={statusInfo.icon} />
                                                {statusInfo.text}
                                    </span>
                                        </Tippy>
                                </div>
                            </div>
                        );
                    })}
                </div>
                )}
            </div>

            {/* Pagination Controls */}
            {pageSize !== 'All' && totalPages > 1 && (
                <div className="pagination">
                    <div className="pagination-info">
                        Page {currentPage + 1} of {totalPages}
                    </div>
                    <div className="pagination-controls">
                        <button 
                            className="pagination-btn"
                            onClick={() => setCurrentPage(0)}
                            disabled={!canPreviousPage}
                        >
                            <i className="fas fa-angle-double-left"></i>
                        </button>
                        <button 
                            className="pagination-btn"
                            onClick={previousPage}
                            disabled={!canPreviousPage}
                        >
                            <i className="fas fa-angle-left"></i>
                        </button>
                        
                        {/* Page numbers */}
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                                pageNum = i + 1;
                            } else if (currentPage <= 2) {
                                pageNum = i + 1;
                            } else if (currentPage >= totalPages - 3) {
                                pageNum = totalPages - 4 + i;
                            } else {
                                pageNum = currentPage - 1 + i;
                            }
                            
                            return (
                                <button
                                    key={pageNum}
                                    className={`pagination-btn ${pageNum === currentPage + 1 ? 'active' : ''}`}
                                    onClick={() => setCurrentPage(pageNum - 1)}
                                >
                                    {pageNum}
                                </button>
                            );
                        })}
                        
                        <button 
                            className="pagination-btn"
                            onClick={nextPage}
                            disabled={!canNextPage}
                        >
                            <i className="fas fa-angle-right"></i>
                        </button>
                        <button 
                            className="pagination-btn"
                            onClick={() => setCurrentPage(totalPages - 1)}
                            disabled={!canNextPage}
                        >
                            <i className="fas fa-angle-double-right"></i>
                        </button>
                    </div>
                </div>
            )}

            {activeModals.map((modal) => (
                modal.isOpen && (
                    <TriageProjectModal
                        key={modal.id}
                        data={modal.data}
                        show={modal.isOpen}
                        onClose={() => handleCloseModal(modal.id)}
                        allowMove={true}
                        style={{
                            top: modal.top,
                            left: modal.left,
                            zIndex: modal.zIndex
                        }}
                    />
                )
            ))}
        </div>
    );
};

export default TriageProjectGrid;
